import {
  type EditorState,
  NodeSelection,
  Plugin,
  PluginKey,
} from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';

import { isBlobUrl } from '../utils/image';

const uploadKey = new PluginKey('upload-image');

export const UploadImagesPlugin = ({ imageClass }: { imageClass: string }) =>
  new Plugin({
    key: uploadKey,
    state: {
      init() {
        return DecorationSet.empty;
      },
      apply(tr, set) {
        set = set.map(tr.mapping, tr.doc);
        // See if the transaction adds or removes any placeholders
        //@ts-expect-error - not yet sure what the type I need here
        const action = tr.getMeta(this);
        if (action?.add) {
          const { id, pos, src } = action.add;

          const placeholder = document.createElement('div');
          placeholder.setAttribute('class', 'img-placeholder');
          const image = document.createElement('img');
          image.setAttribute('class', imageClass);
          image.src = src;
          placeholder.appendChild(image);
          const deco = Decoration.widget(pos + 1, placeholder, {
            id,
          });
          set = set.add(tr.doc, [deco]);
        } else if (action?.remove) {
          set = set.remove(
            set.find(
              undefined,
              undefined,
              (spec) => spec.id === action.remove.id
            )
          );
        }
        return set;
      },
    },
    props: {
      decorations(state) {
        return this.getState(state);
      },
    },
  });

function findPlaceholder(state: EditorState, id: object) {
  const decos = uploadKey.getState(state) as DecorationSet;
  const found = decos.find(undefined, undefined, (spec) => spec.id === id);
  return found.length ? found[0]?.from : null;
}

export interface ImageUploadResult {
  src: string;
  fileId?: string;
}

export interface ImageUploadOptions {
  validateFn?: (file: File) => boolean;
  onUpload: (file: File) => Promise<string | ImageUploadResult>;
}

export const createImageUpload =
  ({ validateFn, onUpload }: ImageUploadOptions): UploadFn =>
  (file, view, pos) => {
    // check if the file is an image
    const validated = validateFn?.(file) ?? true;
    if (!validated) return;
    // A fresh object to act as the ID for this upload
    const id = {};

    // Replace the selection with a placeholder
    const tr = view.state.tr;
    if (!tr.selection.empty) tr.deleteSelection();

    let resolvePlaceholderReady: (() => void) | null = null;
    const placeholderReady = new Promise<void>((resolve) => {
      resolvePlaceholderReady = resolve;
    });
    const markPlaceholderReady = () => {
      if (!resolvePlaceholderReady) return;
      resolvePlaceholderReady();
      resolvePlaceholderReady = null;
    };

    let placeholderAdded = false;
    const reader = new FileReader();
    reader.onload = () => {
      tr.setMeta(uploadKey, {
        add: {
          id,
          pos,
          src: reader.result,
        },
      });
      view.dispatch(tr);
      placeholderAdded = true;
      markPlaceholderReady();
    };
    reader.onerror = markPlaceholderReady;
    reader.onabort = markPlaceholderReady;
    reader.readAsDataURL(file);

    const uploadPromise = Promise.resolve().then(() => onUpload(file));

    uploadPromise.then(
      async (result) => {
        await placeholderReady;
        const { schema } = view.state;

        const pos = findPlaceholder(view.state, id);

        // Otherwise, insert it at the placeholder's position, and remove
        // the placeholder
        const resolved =
          typeof result === 'string'
            ? { src: result, fileId: undefined }
            : result;
        const imageSrc = resolved.src || (reader.result as string | null);
        if (!imageSrc) return;

        const node = schema.nodes.image?.create({
          src: imageSrc,
          fileId: resolved.fileId,
        });
        if (!node) {
          if (isBlobUrl(imageSrc)) URL.revokeObjectURL(imageSrc);
          return;
        }

        // If the content around the placeholder has been deleted, drop
        // the image. If the placeholder never appeared, fall back to
        // inserting at selection.
        if (pos === null) {
          if (placeholderAdded) {
            if (isBlobUrl(imageSrc)) URL.revokeObjectURL(imageSrc);
            return;
          }
          const tr = view.state.tr.replaceSelectionWith(node);
          try {
            tr.setSelection(NodeSelection.create(tr.doc, tr.selection.from));
          } catch {
            // selection may not resolve to a node selection
          }
          view.dispatch(tr);
          return;
        }

        const tr = view.state.tr
          .replaceWith(pos, pos, node)
          .setMeta(uploadKey, { remove: { id } });
        try {
          tr.setSelection(NodeSelection.create(tr.doc, pos));
        } catch {
          // pos may have shifted if ProseMirror restructured the doc
        }
        view.dispatch(tr);

        // Fade in from the pulsing placeholder state
        requestAnimationFrame(() => {
          if (!view.dom.parentNode) return;
          try {
            const domNode = view.nodeDOM(pos);
            if (domNode instanceof HTMLElement) {
              domNode.style.opacity = '0';
              domNode.style.transition = 'opacity 300ms ease-in';
              requestAnimationFrame(() => {
                domNode.style.opacity = '1';
              });
            }
          } catch {
            // view may have been destroyed
          }
        });
      },
      async () => {
        await placeholderReady;
        const pos = findPlaceholder(view.state, id);
        if (pos === null) return;
        // Deletes the image placeholder on error
        const transaction = view.state.tr
          .delete(pos, pos)
          .setMeta(uploadKey, { remove: { id } });
        view.dispatch(transaction);
      }
    );
  };

export type UploadFn = (file: File, view: EditorView, pos: number) => void;

export const handleImagePaste = (
  view: EditorView,
  event: ClipboardEvent,
  uploadFn: UploadFn
) => {
  if (event.clipboardData?.files.length) {
    event.preventDefault();
    const [file] = Array.from(event.clipboardData.files);
    const pos = view.state.selection.from;

    if (file) uploadFn(file, view, pos);
    return true;
  }
  return false;
};

export const handleImageDrop = (
  view: EditorView,
  event: DragEvent,
  moved: boolean,
  uploadFn: UploadFn
) => {
  if (!moved && event.dataTransfer?.files.length) {
    event.preventDefault();
    const [file] = Array.from(event.dataTransfer.files);
    const coordinates = view.posAtCoords({
      left: event.clientX,
      top: event.clientY,
    });
    // Deduct 1 from the pos or else the image will create an extra node.
    // Clamp to 0 and fall back to selection when we don't have coordinates.
    const insertPos =
      coordinates?.pos != null
        ? Math.max(coordinates.pos - 1, 0)
        : view.state.selection.from;
    if (file) uploadFn(file, view, insertPos);
    return true;
  }
  return false;
};
